import { kRestoreAccountEndpoint } from '../components/login/constants';
import {
  ILoginResult,
  IResetPasswordResult,
  SignUpResult,
} from '@ow/shared/login';
import { SignUpParams } from '../context/user-profile-context';
import {
  ILoginService,
  IProfileResponse,
  TokenChangedHandler,
  IUserProfileResult,
} from '../interfaces/services/login-service.interface';
import { AsyncLock } from '../utils/async-lock';
import { CookieUtils } from '../utils/cookie-utils';
import { LoggerService } from '@ow/shared/services/logger.service';

const log = LoggerService.getCategory('LoginService');

// ---------------------------------------------------------------------------
const kAccountsEndpoint = 'https://accounts.overwolf.com';
const kSignupEndpoint = `${kAccountsEndpoint}/users/register`;
const kProfileEndpoint = `${kAccountsEndpoint}/users/profile`;
const kResetPasswordEndpoint = `${kAccountsEndpoint}/auth/action?mode=resetPasswordRequest&email=`;

// ---------------------------------------------------------------------------
export class LoginService implements ILoginService {
  private readonly config: any;
  private userProfile: IProfileResponse;
  private lock: AsyncLock;
  private tokenChangedHandlers: TokenChangedHandler[];

  // ---------------------------------------------------------------------------
  constructor(config: any) {
    this.config = config;
    this.lock = new AsyncLock();
    this.tokenChangedHandlers = [];
  }

  // ---------------------------------------------------------------------------
  public async getUserProfile(
    token: string,
    refreshRequired: boolean = false,
  ): Promise<IUserProfileResult> {
    if (!this.userProfile) {
      await this.lock.lock();
    }

    return this.fetchUserProfile(token, refreshRequired);
  }

  // ---------------------------------------------------------------------------
  public async restoreAccount(): Promise<boolean> {
    const token = this.getToken();
    try {
      const restoreAccountResponse = await fetch(kRestoreAccountEndpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      log.info(
        `restore account response: ${JSON.stringify(restoreAccountResponse)}`,
      );

      if (restoreAccountResponse.ok) {
        return true;
      }

      return false;
    } catch (error) {
      log.error(`restore account error: ${JSON.stringify(error)}`);
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  public async resetPassword(email: string): Promise<IResetPasswordResult> {
    try {
      email = encodeURIComponent(email);
      const result = await fetch(`${kResetPasswordEndpoint}${email}`);
      log.info(`forgot password success: ${JSON.stringify(result)}`);
      if (result.status === 200) {
        return {
          success: true,
        };
      }

      return {
        success: false,
        statusCode: result.status,
      };
    } catch (error) {
      log.error(`forgot password error: ${JSON.stringify(error)}`);
      return {
        success: false,
        statusCode: error.status,
      };
    }
  }

  // ---------------------------------------------------------------------------
  public async performOverwolfLogin(
    email: string,
    password: string,
  ): Promise<ILoginResult> {
    try {
      const loginResponse = await fetch(`${kAccountsEndpoint}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: email, password }),
      });

      if (!loginResponse.ok) {
        let errorResponse;
        try {
          errorResponse = await loginResponse.json();
        } catch {}

        const cause = errorResponse?.message
          ? errorResponse.message
          : (loginResponse.statusText ?? 'Unknown error during login');
        throw new Error(cause);
      }

      const loginJson = await loginResponse.json();
      const token = loginJson.token;
      return { status: 'success', error: null, token };
    } catch (error) {
      log.error(`login server error: ${JSON.stringify(error)}`);
      return {
        status: 'failed',
        error: error.message,
      };
    }
  }

  // ---------------------------------------------------------------------------
  public async performOverwolfSignUp(
    signUpParams: SignUpParams,
  ): Promise<SignUpResult> {
    try {
      const result = await fetch(kSignupEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signUpParams),
      });

      const data = await result.json();

      let success: boolean = true;
      if (!result.ok) {
        success = false;
      }

      const token = data?.token;
      return {
        success,
        token,
        error: success ? null : data.message,
      };
    } catch (error) {
      log.error(`signup server error: ${JSON.stringify(error.response)}`);
      return {
        success: false,
        error: error.response,
      };
    }
  }

  // ---------------------------------------------------------------------------
  public setToken(token: string): void {
    CookieUtils.setCookie('owUser', token);
    this.notifyTokenChanged(token);
  }

  // ---------------------------------------------------------------------------
  public getToken(): string {
    return CookieUtils.getCookie('owUser');
  }

  // ---------------------------------------------------------------------------
  public onTokenChanged(handler: TokenChangedHandler): () => void {
    this.tokenChangedHandlers = [...this.tokenChangedHandlers, handler];

    return () => {
      this.tokenChangedHandlers = this.tokenChangedHandlers.filter(
        existingHandler => existingHandler !== handler
      );
    };
  }

  // ---------------------------------------------------------------------------
  public logOut(): void {
    CookieUtils.deleteCookie('owUser');
    this.userProfile = null;
    this.lock = new AsyncLock();
    this.notifyTokenChanged(null);
  }

  // ---------------------------------------------------------------------------
  private notifyTokenChanged(token: string | null): void {
    this.tokenChangedHandlers.forEach((handler) => {
      handler(token);
    });
  }

  // ---------------------------------------------------------------------------
  private async fetchUserProfile(
    token: string,
    refreshRequired = false,
  ): Promise<IUserProfileResult> {
    try {
      if (this.userProfile && !refreshRequired) {
        return { profile: this.userProfile, status: 'success', token };
      }

      const profileResponse = await fetch(kProfileEndpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!profileResponse.ok) {
        throw new Error('Error getting profile', {
          cause: profileResponse.statusText,
        });
      }

      const profileResult = await profileResponse.json();
      this.userProfile = profileResult;
      await this.lock.unlock();

      return { profile: this.userProfile, status: 'success', token };
    } catch (error) {
      log.error(`get profile error: ${JSON.stringify(error)}`);
      return null;
    }
  }
}
